import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { disciplines, projects, projectsByDiscipline } from "../data/projects";
import { Reveal, MaskLines } from "./ui/Primitives";
import ProjectCard from "./ui/ProjectCard";

const Portfolio: React.FC = () => {
  const [activeId, setActiveId] = useState(disciplines[0].id);

  // The jump bar follows the reader rather than the reader chasing the bar.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id as typeof activeId);
      },
      { rootMargin: "-25% 0px -60% 0px", threshold: 0 }
    );

    disciplines.forEach((d) => {
      const el = document.getElementById(d.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Page opening */}
      <section className="bg-ink pb-16 pt-28 lg:pb-20 lg:pt-36">
        <div className="shell">
          <MaskLines
            as="h1"
            className="t-display max-w-[13ch] text-fg"
            lines={["אל תסמכו", "על מילים"]}
          />
          <Reveal delay={140}>
            <p className="t-lead mt-8 max-w-[46ch]">
              כל פרויקט כאן חי ברשת עכשיו. לחצו, גלשו, בדקו מהירות, בדקו מובייל,
              ותשפטו לפי מה שאתם רואים.
            </p>
          </Reveal>

          <Reveal delay={220}>
            <dl className="mt-14 grid grid-cols-2 gap-px border border-[var(--line)] bg-[var(--line)] sm:grid-cols-4">
              {disciplines.map((d) => (
                <div key={d.id} className="bg-ink p-5">
                  <dt className="t-data text-3xl text-signal" dir="ltr">
                    {String(projectsByDiscipline(d.id).length).padStart(2, "0")}
                  </dt>
                  <dd className="mt-1 text-sm text-fg-2">{d.label}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      {/* Jump bar */}
      <div className="sticky top-[3.75rem] z-30 border-y border-[var(--line)] bg-ink/92 backdrop-blur-xl">
        <div className="shell">
          <nav
            aria-label="דילוג לפי תחום"
            className="-mx-1 flex gap-1 overflow-x-auto py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {disciplines.map((d) => (
              <a
                key={d.id}
                href={`#${d.id}`}
                aria-current={activeId === d.id ? "true" : undefined}
                className={`shrink-0 whitespace-nowrap px-3 py-1.5 text-[0.85rem] transition-colors duration-200 ${
                  activeId === d.id
                    ? "text-signal"
                    : "text-fg-3 hover:text-fg"
                }`}
              >
                {d.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* Chapters */}
      {disciplines.map((discipline, chapterIndex) => {
        const items = projectsByDiscipline(discipline.id);
        if (items.length === 0) return null;

        return (
          <section
            key={discipline.id}
            id={discipline.id}
            className={`scroll-mt-32 py-20 lg:py-28 ${
              chapterIndex % 2 === 0 ? "bg-ink" : "bg-ink-2"
            }`}
          >
            <div className="shell">
              <Reveal>
                <div className="mb-12 border-t border-[var(--line)] pt-6">
                  <div className="flex items-baseline gap-4">
                    <span className="t-data text-sm text-signal" dir="ltr">
                      {String(chapterIndex + 1).padStart(2, "0")}
                    </span>
                    <h2 className="t-h2 text-fg">{discipline.label}</h2>
                    <span className="t-data ms-auto hidden text-xs text-fg-3 sm:block" dir="ltr">
                      {items.length}
                    </span>
                  </div>
                  <p className="t-body mt-4 max-w-[54ch]">{discipline.blurb}</p>
                </div>
              </Reveal>

              <div className="grid gap-x-8 gap-y-14 md:grid-cols-2">
                {items.map((project, i) => (
                  <Reveal key={project.title} delay={(i % 2) * 90}>
                    <ProjectCard project={project} />
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Close */}
      <section className="border-t border-[var(--line)] bg-ink py-24">
        <div className="shell">
          <MaskLines
            as="h2"
            className="t-h1 max-w-[18ch] text-fg"
            lines={["הפרויקט הבא", "יכול להיות שלכם"]}
          />
          <Reveal delay={140}>
            <p className="t-lead mt-6 max-w-[48ch]">
              {projects.length} פרויקטים חיים ברשת, בארבעה תחומים שונים. ספרו לנו
              מה אתם צריכים ונחזור אליכם עם הצעה מותאמת.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link to="/contact" className="btn btn-primary mt-10">
              <span>בואו נדבר</span>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Portfolio;
